import { useEffect, useState } from 'react';
import useWindow from './useWindow';

export const MEDIAQUERY_DEFAULTS = {
  xs: '0',
  xsMax: '575',
  sm: '576',
  smMax: '767',
  md: '768',
  mdMax: '991',
  lg: '992',
  lgMax: '1199',
  xl: '1200',
  xlMax: '1399',
  xxl: '1400'
} as const;

export type QueryOpts = {
  type: 'min' | 'max';
  query: typeof MEDIAQUERY_DEFAULTS[keyof typeof MEDIAQUERY_DEFAULTS] | string;
};

export type MediaQueryParamsType = string | QueryOpts | [QueryOpts, QueryOpts];

const getQueryString = (query: QueryOpts | [QueryOpts, QueryOpts]): string => {
  return Array.isArray(query)
    ? `${query.reduce((total, current, idx) => {
        return `${total}(${current.type}-width: ${current.query}px)${idx === 0 ? ' and ' : ''}`;
      }, '')}`
    : `(${query.type}-width: ${query.query}px)`;
};

const useMediaQuery = (query: MediaQueryParamsType, overrideNativeWindow?: boolean): boolean => {
  const window = useWindow(overrideNativeWindow);
  const queryString = typeof query === 'string' ? query : getQueryString(query);
  const mediaQuery = window?.matchMedia(queryString);
  const [match, setMatch] = useState<boolean>(mediaQuery?.matches || false);

  useEffect(() => {
    const handler = (): void => setMatch(mediaQuery?.matches || false);
    mediaQuery?.addListener(handler);
    handler();

    return (): void => mediaQuery?.removeListener(handler);
  }, [mediaQuery]);

  return match;
};

export default useMediaQuery;
